/**
 * フォルダー選択の共通整形。
 *
 * これまで選択肢は名前だけの平らな一覧で、「図面」が技術の下なのか製造の下なのか
 * 分からなかった（同名フォルダーもある）。木の順番に並べ、親からの道順を見せる。
 */

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'ja',
  { numeric: true, sensitivity: 'base' });

/** 木の順（親 → その子）に並べ、depth と親からの道順を付けて返す。 */
export function flattenFolders(folders = []) {
  const kids = new Map();
  const known = new Set(folders.map((f) => f.id));
  folders.forEach((f) => {
    // 親が見えない（権限外・削除済み）フォルダーはルート扱いにして、迷子にしない
    const p = f.parent_folder_id && known.has(f.parent_folder_id) ? f.parent_folder_id : '';
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(f);
  });
  const out = [];
  const walk = (parentId, depth, trail) => {
    (kids.get(parentId) || []).sort(byName).forEach((f) => {
      const path = [...trail, f.name];
      out.push({ ...f, depth, path, pathLabel: path.join(' / ') });
      walk(f.id, depth + 1, path);
    });
  };
  walk('', 0, []);
  return out;
}

/** そのフォルダー自身と、その配下すべての id。移動先から外すために使う。 */
export function folderSubtreeIds(folders = [], rootId) {
  if (!rootId) return new Set();
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    folders.forEach((f) => {
      if (f.parent_folder_id && ids.has(f.parent_folder_id) && !ids.has(f.id)) {
        ids.add(f.id);
        grew = true;
      }
    });
  }
  return ids;
}

/**
 * <select> / Combobox 用の選択肢。
 * - `label`: 階層が分かる表示（例: 「　└ 図面」）
 * - `pathLabel`: 親からの道順（例: 「技術 / 図面」）— 検索や補助表示に使う
 */
export function folderOptions(folders = [], { excludeSubtreeOf, excludeIds } = {}) {
  const skip = excludeSubtreeOf
    ? folderSubtreeIds(folders, excludeSubtreeOf)
    : new Set(excludeIds || []);
  return flattenFolders(folders)
    .filter((f) => !skip.has(f.id))
    .map((f) => ({
      value: f.id,
      label: `${'　'.repeat(f.depth)}${f.depth ? '└ ' : ''}${f.name}`,
      name: f.name,
      pathLabel: f.pathLabel,
      depth: f.depth,
    }));
}

/** そのフォルダーの道順（例: 「技術 / 図面 / AOP」）。見つからなければ null。 */
export function folderPathLabel(folders = [], folderId) {
  if (!folderId) return null;
  const hit = flattenFolders(folders).find((f) => f.id === folderId);
  return hit ? hit.pathLabel : null;
}
